import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  changeWorkshopSales,
  WorkshopSalesError,
  type WorkshopSalesAction,
} from '../../../api/workshop-sales';

import { invalidateLists, workshopKey } from './useWorkshop';

/** What the operator reads when a disable / re-enable is refused. */
export function workshopSalesErrorMessage(err: unknown, action: WorkshopSalesAction): string {
  const verb = action === 'disable' ? 'disable' : 're-enable';
  if (!(err instanceof WorkshopSalesError)) return `Could not ${verb} the workshop. Try again.`;
  switch (err.status) {
    case 400:
      return 'This workshop link is not valid. Reload the page and try again.';
    case 403:
      return `You don’t have permission to ${verb} this workshop.`;
    case 404:
      return 'This workshop no longer exists.';
    case 409:
      return `${err.message}.`;
    default:
      return `Could not ${verb} the workshop. Try again.`;
  }
}

export interface WorkshopSales {
  run: (action: WorkshopSalesAction) => Promise<boolean>;
  isPending: boolean;
  errorMessage: string | null;
}

/**
 * Disable or re-enable a workshop. On success the fresh workshop replaces the
 * cached one; when the server says the state moved underneath us (409) or the
 * workshop is gone (404) the cache is refetched so the page stops offering a
 * stale action. `run` resolves true on success.
 */
export function useWorkshopSales(id: string): WorkshopSales {
  const qc = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (action: WorkshopSalesAction) => changeWorkshopSales(id, action),
    onSuccess: (workshop) => {
      qc.setQueryData(workshopKey(id), workshop);
      invalidateLists(qc);
    },
    onError: (err) => {
      if (err instanceof WorkshopSalesError && (err.status === 404 || err.status === 409)) {
        qc.invalidateQueries({ queryKey: workshopKey(id) });
      }
    },
  });

  const run = async (action: WorkshopSalesAction): Promise<boolean> => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync(action);
      return true;
    } catch (err) {
      setErrorMessage(workshopSalesErrorMessage(err, action));
      return false;
    }
  };

  return { run, isPending: mutation.isPending, errorMessage };
}
